import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { initializeStrapiBackend } from '../Contentful_API/strapiBackend';
import './SubscriptionForm.css';

const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeTopics(data) {
    if (!Array.isArray(data)) throw new Error('Subscription topics are unavailable.');

    // GET /subscriptions/topics returns enabled topics, already sorted, as { key, label }.
    return data
        .map(item => item?.attributes ? { id: item.id, ...item.attributes } : item)
        .map(topic => topic && !topic.topicKey && topic.key ? { ...topic, topicKey: topic.key } : topic)
        .filter(topic => topic && topic.enabled !== false &&
            typeof topic.topicKey === 'string' && topic.topicKey.trim() &&
            typeof topic.label === 'string' && topic.label.trim())
        .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
}

export default function SubscriptionForm() {
    const [topics, setTopics] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const submitting = useRef(false);

    useEffect(() => {
        if (!open) return undefined;

        const controller = new AbortController();
        let active = true;

        async function loadTopics() {
            setLoading(true);
            setLoadError(false);
            try {
                const { apiUrl } = await initializeStrapiBackend();
                const response = await fetch(`${apiUrl}/subscriptions/topics`, {
                    credentials: 'omit',
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error('Subscription topics could not be loaded.');
                const result = await response.json();
                const availableTopics = normalizeTopics(result.data);
                if (active) setTopics(availableTopics);
            } catch {
                if (active) setLoadError(true);
            } finally {
                if (active) setLoading(false);
            }
        }

        loadTopics();
        return () => {
            active = false;
            controller.abort();
        };
    }, [attempt, open]);

    function updateEmail(event) {
        setEmail(event.target.value);
        setError('');
        setSuccess(false);
    }

    function toggleTopic(topicKey) {
        setSelectedTopics(current => current.includes(topicKey)
            ? current.filter(key => key !== topicKey)
            : [...current, topicKey]);
        setError('');
        setSuccess(false);
    }

    async function submit(event) {
        event.preventDefault();
        if (submitting.current) return;
        setError('');
        setSuccess(false);

        const submittedEmail = email.trim();
        if (!validEmail.test(submittedEmail) || submittedEmail.length > 254) {
            setError('Enter a valid email address.');
            return;
        }
        if (!consent) {
            setError('Please agree to receive email updates before subscribing.');
            return;
        }
        if (loading || loadError || !topics.length ||
            !selectedTopics.length ||
            selectedTopics.some(key => !topics.some(topic => topic.topicKey === key))) {
            setError('Choose at least one available topic.');
            return;
        }

        submitting.current = true;
        setBusy(true);
        try {
            const { apiUrl } = await initializeStrapiBackend();
            const response = await fetch(`${apiUrl}/subscriptions`, {
                method: 'POST',
                credentials: 'omit',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: submittedEmail,
                    topics: selectedTopics,
                    consent: true,
                }),
            });

            if (response.status === 429) {
                setError('Too many attempts. Please wait a minute and try again.');
            } else if (response.status === 400) {
                const result = await response.json();
                const message = result?.error?.message || result?.message || '';
                if (/SMTP server rejected the email content or sending policy/i.test(message)) {
                    setError('Your request reached the subscription service, but it could not send the confirmation email. Please try again later or contact the Ministry if the problem continues.');
                } else {
                    setError('Your subscription could not be accepted. Check your details and selected topics, then try again.');
                }
            } else if (!response.ok) {
                setError('We could not confirm your subscription request. Please try again later.');
            } else {
                setEmail('');
                setSelectedTopics([]);
                setConsent(false);
                setSuccess(true);
            }
        } catch {
            setError('We could not confirm your subscription request. Check your connection before trying again.');
        } finally {
            submitting.current = false;
            setBusy(false);
        }
    }

    return (
        <>
            <aside className="portal-subscription-banner" aria-labelledby="portal-subscription-banner-title">
                <div className="portal-subscription-banner__copy">
                    <span className="portal-subscription-banner__eyebrow">Ministry email updates</span>
                    <h2 id="portal-subscription-banner-title">Stay informed</h2>
                    <p>Get the latest news, publications and updates that matter to you.</p>
                </div>
                <button
                    type="button"
                    className="portal-subscription-banner__button"
                    onClick={() => {
                        setError('');
                        setSuccess(false);
                        setOpen(true);
                    }}
                >
                    Subscribe to updates
                    <span aria-hidden="true">→</span>
                </button>
            </aside>

            <Dialog
                open={open}
                onClose={() => { if (!busy) setOpen(false); }}
                fullWidth
                maxWidth="sm"
                aria-labelledby="portal-subscription-title"
            >
                <DialogTitle disableTypography className="portal-subscription-dialog__title">
                    <div>
                        <h2 id="portal-subscription-title">Stay informed</h2>
                        <p>Choose the Ministry updates you would like to receive.</p>
                    </div>
                    <IconButton
                        aria-label="Close subscription form"
                        onClick={() => setOpen(false)}
                        disabled={busy}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="portal-subscription-dialog__content">
                    <form onSubmit={submit} aria-busy={busy} className="portal-subscription__form">
                        <fieldset disabled={busy} className="portal-subscription__fields">
                            <label className="portal-subscription__label" htmlFor="subscription-email">Email address</label>
                            <input
                                id="subscription-email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                maxLength={254}
                                value={email}
                                onChange={updateEmail}
                                placeholder="you@example.com"
                            />

                            <fieldset className="portal-subscription__topics" aria-describedby="subscription-topic-help">
                                <legend className="portal-subscription__label">Topics</legend>
                                <p id="subscription-topic-help" className="portal-subscription__help">
                                    Select at least one topic.
                                </p>
                                {loading && <p role="status">Loading topics…</p>}
                                {loadError && (
                                    <div role="alert" className="portal-subscription__error">
                                        Topics could not be loaded.{' '}
                                        <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
                                    </div>
                                )}
                                {!loading && !loadError && topics.length === 0 && (
                                    <p role="status">There are no subscription topics available right now.</p>
                                )}
                                {!loading && !loadError && topics.map(topic => (
                                    <label className="portal-subscription__topic" key={topic.topicKey}>
                                        <input
                                            type="checkbox"
                                            value={topic.topicKey}
                                            checked={selectedTopics.includes(topic.topicKey)}
                                            onChange={() => toggleTopic(topic.topicKey)}
                                        />
                                        <span>{topic.label}</span>
                                    </label>
                                ))}
                            </fieldset>

                            <label className="portal-subscription__consent">
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={event => {
                                        setConsent(event.target.checked);
                                        setError('');
                                        setSuccess(false);
                                    }}
                                    required
                                />
                                <span>I agree to receive email updates about the topics selected above.</span>
                            </label>

                            <button
                                className="portal-subscription__submit"
                                type="submit"
                                disabled={busy || loading || loadError || !topics.length}
                            >
                                {busy ? 'Submitting…' : 'Subscribe'}
                            </button>
                        </fieldset>
                        {error && <p role="alert" className="portal-subscription__error">{error}</p>}
                        {success && (
                            <p role="status" className="portal-subscription__success">
                                Thank you. If this address can be subscribed, a confirmation email will be sent.
                            </p>
                        )}
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
